import { forwardRef } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/base/Button';
import { typography } from '@/styles/typography';
import { ArrowRight } from 'lucide-react';
import CityIcon from '@/icons/city.svg?react';
import AppleIcon from '@/icons/apple.svg?react';
import HospitalIcon from '@/icons/hospital.svg?react';
import UsersIcon from '@/icons/users.svg?react';
import NetworkIcon from '@/icons/network.svg?react';
import DollyIcon from '@/icons/dolly.svg?react';

/**
 * Content interface for a single state plan category circle
 */
export interface StatePlanCategory {
  /** Unique identifier for the category */
  id: string;
  /** Display label text */
  label: string;
  /** Icon name key (maps to SVG icon component) */
  icon: string;
  /** Optional link destination URL — when absent, circle renders as decorative (non-clickable) */
  href?: string;
}

/**
 * Content interface for the StatePlanBanner component
 */
export interface StatePlanBannerContent {
  /** Section heading text */
  heading: string;
  /** Array of category circles to display */
  categories: StatePlanCategory[];
  /** CTA button label text */
  ctaLabel: string;
  /** CTA button link destination */
  ctaHref: string;
  /** Optional CTA icon name */
  ctaIcon?: string;
}

/**
 * Props interface for StatePlanBanner component
 */
export interface StatePlanBannerProps {
  /** Banner content - renders nothing when undefined */
  content?: StatePlanBannerContent;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Map of icon string names to their SVG React components
 */
const iconMap: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  city: CityIcon,
  apple: AppleIcon,
  hospital: HospitalIcon,
  users: UsersIcon,
  network: NetworkIcon,
  dolly: DollyIcon,
};

/**
 * StatePlanBanner - Section promoting Ohio's State Plan on Aging
 *
 * Displays a heading, a row of 6 circular category icons with labels
 * (each as a clickable link), and a secondary CTA button to explore
 * the State Plan.
 *
 * @example
 * ```tsx
 * import { StatePlanBanner } from '@/components';
 *
 * <StatePlanBanner
 *   content={{
 *     heading: "Learn more about our state plan on aging",
 *     categories: [
 *       { id: "community", label: "Community conditions", icon: "city" },
 *     ],
 *     ctaLabel: "Explore our State Plan on Aging",
 *     ctaHref: "https://aging.ohio.gov/about-us/reports-and-data/ohios-state-plan"
 *   }}
 * />
 * ```
 */
export const StatePlanBanner = forwardRef<HTMLElement, StatePlanBannerProps>(
  ({ content, className }, ref) => {
    if (!content) return null;

    return (
      <section
        ref={ref}
        className={cn(
          'w-full bg-white pt-[60px] px-5 md:px-10 pb-8',
          className
        )}
        aria-labelledby="state-plan-heading"
      >
        <div className="max-w-[1360px] mx-auto flex flex-col items-start md:items-center gap-[52px]">
          {/* Section Heading */}
          <h2
            id="state-plan-heading"
            className={cn(
              typography.headingSmall,
              'text-left md:text-center text-[#000000]'
            )}
          >
            {content.heading}
          </h2>

          {/* Circle Group */}
          <ul
            className={cn(
              'w-full max-w-[1360px] list-none',
              'grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6',
              'gap-8',
              'place-items-center'
            )}
          >
            {content.categories.map((category) => {
              const IconComponent = iconMap[category.icon];
              const circleClasses = cn(
                'w-[200px] max-w-full aspect-square',
                'rounded-full',
                'bg-[#F2F2F2]',
                'flex flex-col items-center justify-center',
                'gap-2 p-2'
              );

              const circleContent = (
                <>
                  {IconComponent && (
                    <IconComponent
                      className="w-10 h-10 text-[#0A4D8F] shrink-0"
                      aria-hidden="true"
                    />
                  )}
                  <span
                    className={cn(
                      typography.subtitle,
                      'text-center text-[#000000] text-[16px] leading-[22px] md:text-[20px] md:leading-[28px]'
                    )}
                  >
                    {category.label}
                  </span>
                </>
              );

              if (category.href) {
                const isExternal = category.href.startsWith('http');
                return (
                  <li key={category.id} className="place-self-center">
                    <a
                      href={category.href}
                      className={cn(
                        circleClasses,
                        'no-underline',
                        'hover:opacity-80 transition-opacity',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A4D8F] focus-visible:ring-offset-2'
                      )}
                      aria-label={category.label}
                      {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                    >
                      {circleContent}
                    </a>
                  </li>
                );
              }

              return (
                <li key={category.id} className="place-self-center">
                  <div
                    className={circleClasses}
                    aria-label={category.label}
                  >
                    {circleContent}
                  </div>
                </li>
              );
            })}
          </ul>

          {/* CTA Button */}
          <div className="w-full flex justify-center">
            <Button
              variant="secondary"
              size="large"
              asChild
              className="w-full md:w-[348px] h-14"
            >
              <a
                href={content.ctaHref}
                {...(content.ctaHref.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              >
                {content.ctaLabel}
                <ArrowRight className="h-6 w-6 ml-2" aria-hidden="true" />
              </a>
            </Button>
          </div>
        </div>
      </section>
    );
  }
);

StatePlanBanner.displayName = 'StatePlanBanner';
